import { useState } from 'react';
import { Alert, Button, Card, Spinner, Table } from 'react-bootstrap';
import { QuotaReportService } from '@/services/quotaReportService';
import { ProgressTracker } from './ProgressTracker';
import { reasonOf } from './clientSelectionUtils';
import type { QuotaDescriptor, QuotaMetric, ToolProgress } from '@/types/tools';
import './QuotaCheckCard.css';

interface QuotaCheckCardProps {
  accountId: string;
  region: string;
  disabled?: boolean;
}

const METRIC_WORDS: Record<QuotaMetric, string> = {
  'requests-per-minute': 'Requests a minute',
  'tokens-per-minute': 'Tokens a minute',
  'requests-per-day': 'Requests a day',
  'tokens-per-day': 'Tokens a day',
};

/** Reads the Bedrock request limits of one AWS account in one region, on request. */
export function QuotaCheckCard({ accountId, region, disabled }: QuotaCheckCardProps) {
  const [quotas, setQuotas] = useState<QuotaDescriptor[]>([]);
  const [values, setValues] = useState<Record<string, number | null>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ToolProgress | null>(null);
  const [hasRun, setHasRun] = useState(false);

  const handleCheck = async () => {
    setLoading(true);
    setError(null);
    setHasRun(true);
    try {
      const result = await QuotaReportService.checkSingleAccountQuotas({
        accountId,
        region,
        onProgress: setProgress,
      });
      setQuotas(result.quotas);
      setValues(result.values);
    } catch (err) {
      setError(reasonOf(err));
    } finally {
      setLoading(false);
    }
  };

  const priorityQuotas = quotas.filter((q) => q.isPriority);
  const otherQuotas = quotas.filter((q) => !q.isPriority);

  const formatValue = (v: number | null | undefined): string => {
    if (v === null || v === undefined) return '';
    return v.toLocaleString();
  };

  const renderQuotaRows = (items: QuotaDescriptor[], highlight: boolean) =>
    items.map((q) => (
      <tr key={q.QuotaCode} className={highlight ? 'quota-check__row--key' : undefined}>
        <td title={q.QuotaName}>{q.Model}</td>
        <td>{q.Type}</td>
        <td>{METRIC_WORDS[q.Metric]}</td>
        <td className="text-end numeric">{formatValue(values[q.QuotaCode])}</td>
      </tr>
    ));

  return (
    <Card className="quota-check">
      <Card.Header className="quota-check__head">
        <div>
          <h2 className="quota-check__title">Bedrock limits</h2>
          <p className="quota-check__sub">
            The request limits of account <span className="mono">{accountId || 'not set'}</span> in{' '}
            <span className="mono">{region || 'no region'}</span>.
          </p>
        </div>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={handleCheck}
          disabled={disabled || loading || !accountId || !region}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-1" />
              Checking
            </>
          ) : hasRun ? (
            'Check again'
          ) : (
            'Check limits'
          )}
        </Button>
      </Card.Header>

      {loading && progress && (
        <Card.Body>
          <ProgressTracker status="running" progress={progress} />
        </Card.Body>
      )}

      {!loading && error && (
        <Card.Body>
          <Alert variant="danger" className="mb-0">
            The limits could not be read. {error} Check the account ID and the region, then check again.
          </Alert>
        </Card.Body>
      )}

      {!loading && !error && hasRun && quotas.length === 0 && (
        <Card.Body className="quota-check__empty">
          Bedrock returned no limits for this account in this region.
        </Card.Body>
      )}

      {!loading && quotas.length > 0 && (
        <>
          {priorityQuotas.length > 0 && (
            <p className="quota-check__note">
              The newer models come first. For one of them, 50 requests a minute or more is a healthy limit.
            </p>
          )}
          <div className="quota-check__scroll">
            <Table hover size="sm" className="mb-0 quota-check__table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Route</th>
                  <th>Limit</th>
                  <th className="text-end">Value</th>
                </tr>
              </thead>
              <tbody>
                {priorityQuotas.length > 0 && (
                  <>
                    <tr className="quota-check__group">
                      <td colSpan={4}>
                        Newer models, version 4.5 and later
                        <span className="quota-check__group-count">{priorityQuotas.length}</span>
                      </td>
                    </tr>
                    {renderQuotaRows(priorityQuotas, true)}
                  </>
                )}
                {otherQuotas.length > 0 && (
                  <>
                    <tr className="quota-check__group">
                      <td colSpan={4}>
                        Other models
                        <span className="quota-check__group-count">{otherQuotas.length}</span>
                      </td>
                    </tr>
                    {renderQuotaRows(otherQuotas, false)}
                  </>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </Card>
  );
}
